import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart3,
  Users,
  FileText,
  Award,
  TrendingUp,
  Eye,
  Edit3,
  Calendar,
  Globe,
  Activity
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalServices: 0,
    totalProducts: 0,
    totalTestimonials: 0,
    totalAwards: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch data from various endpoints to build dashboard stats
      const [servicesRes, heroStatsRes] = await Promise.all([
        axios.get(`${API_URL}/api/services`),
        axios.get(`${API_URL}/api/hero-stats`)
      ]);

      setStats({
        totalServices: servicesRes.data.data?.length || 0,
        totalProducts: 5, // From mock data
        totalTestimonials: 3, // From mock data
        totalAwards: 3 // From mock data
      });

      // Mock recent activity
      setRecentActivity([
        {
          id: 1,
          action: 'Updated company settings',
          time: '2 hours ago',
          icon: Edit3,
          color: 'text-blue-600'
        },
        {
          id: 2,
          action: 'Added new service',
          time: '1 day ago',
          icon: FileText,
          color: 'text-green-600'
        },
        {
          id: 3,
          action: 'Updated hero statistics',
          time: '2 days ago',
          icon: BarChart3,
          color: 'text-purple-600'
        }
      ]);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Services',
      value: stats.totalServices,
      icon: FileText,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      description: 'Active service offerings'
    },
    {
      title: 'Products',
      value: stats.totalProducts,
      icon: Award,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      description: 'Product portfolio'
    },
    {
      title: 'Testimonials',
      value: stats.totalTestimonials,
      icon: Users,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      description: 'Client testimonials'
    },
    {
      title: 'Contract Awards',
      value: stats.totalAwards,
      icon: TrendingUp,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600',
      description: 'Government contracts'
    }
  ];

  const quickActions = [
    {
      title: 'Edit Company Info',
      description: 'Update company details and hero section',
      icon: Edit3,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      href: '/admin/company'
    },
    {
      title: 'Manage Services',
      description: 'Add, edit, or reorder services',
      icon: FileText,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      href: '/admin/services'
    },
    {
      title: 'View Website',
      description: 'Preview the live website',
      icon: Eye,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      href: '/'
    },
    {
      title: 'Media Center',
      description: 'Manage blog posts and news',
      icon: Globe,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      href: '/admin/media'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white bg-opacity-10 rounded-full -translate-y-32 translate-x-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white bg-opacity-10 rounded-full translate-y-24 -translate-x-24"></div>
        <div className="relative">
          <h1 className="text-3xl font-bold mb-2">Welcome to your Dashboard</h1>
          <p className="text-blue-100 text-lg">
            Manage your Blu Digital Technologies website content with ease
          </p>
          <div className="mt-6 flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <Activity className="h-5 w-5 text-blue-200" />
              <span className="text-blue-100">System Status: Online</span>
            </div>
            <div className="flex items-center space-x-2">
              <Calendar className="h-5 w-5 text-blue-200" />
              <span className="text-blue-100">Last Updated: Today</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <div key={index} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-xs text-gray-500 mt-1">{stat.description}</p>
              </div>
              <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                <stat.icon className={`h-6 w-6 ${stat.textColor}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Quick Actions */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <Edit3 className="h-5 w-5 mr-2 text-blue-600" />
            Quick Actions
          </h2>
          <div className="space-y-4">
            {quickActions.map((action, index) => (
              <button
                key={index}
                onClick={() => window.location.href = action.href}
                className="w-full flex items-center p-4 rounded-xl hover:bg-gray-50 transition-all duration-200 text-left border border-gray-100 hover:border-gray-200 group"
              >
                <div className={`w-12 h-12 rounded-xl ${action.bgColor} flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-200`}>
                  <action.icon className={`h-6 w-6 ${action.color}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                    {action.title}
                  </h3>
                  <p className="text-sm text-gray-600">{action.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <Activity className="h-5 w-5 mr-2 text-blue-600" />
            Recent Activity
          </h2>
          <div className="space-y-4">
            {recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-center space-x-4 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <activity.icon className={`h-5 w-5 ${activity.color}`} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                  <p className="text-xs text-gray-500">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-6 text-center">
            <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              View All Activity
            </button>
          </div>
        </div>
      </div>

      {/* Website Preview */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <Globe className="h-5 w-5 mr-2 text-blue-600" />
          Website Preview
        </h2>
        <div className="bg-gray-100 rounded-xl p-4 text-center">
          <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">Preview your live website to see how changes appear to visitors</p>
          <button 
            onClick={() => window.open('/', '_blank')}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105"
          >
            <Eye className="h-5 w-5 mr-2" />
            Open Website
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;